import type { Url } from 'next/dist/shared/lib/router/router';
import type { LinkProps } from 'next/link';
import type { CSSProperties } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { cn } from '@poseidon/components/cn';
import React from 'react';

import { Arrows, General } from '@poseidon/components/StrokeIcons';
import { Tooltip } from '@poseidon/components/Input2';

import NotificationLabel from '~/components/NotificationLabel';
import { ButtonLink, Button } from '@poseidon/components/Input2';

type RowProps = LinkProps & {
  icon: React.JSX.Element;
  children?: React.ReactNode;
  className?: string;
  minimized?: boolean;
  counterValue?: number;
  subLinks?: RowProps[];
  addLink?: Url;
  addLinkLabel?: string;
  newBadge?: boolean;
  gradientBadge?: boolean;
  gradientActive?: boolean;
  loading?: boolean;
  processingCount?: number;
};

export const LeftNavRow = ({
  href,
  icon,
  className,
  children,
  minimized,
  counterValue,
  subLinks,
  addLink,
  addLinkLabel,
  newBadge,
  gradientBadge,
  gradientActive,
  loading,
  ...props
}: RowProps) => {
  const { pathname } = useRouter();
  const subLinksRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(() => {
    return getSublinksUrls(subLinks).includes(pathname);
  });

  const hrefString = typeof href === 'string' ? href : href?.pathname ?? '';

  const isActive = pathname === hrefString;
  const isPartialActive = pathname.startsWith(hrefString);

  const classList = cn(
    className,
    // Base row styles
    'flex flex-row relative font-normal text-base leading-6 gap-3 p-2 no-underline rounded-md h-10 items-center shrink-0 text-(--nav-txt-color) transition-all duration-200 overflow-hidden',
    // Hover state
    'hover:bg-(--nav-hover-bg-color)',
    // Active states
    (isActive || isPartialActive) && 'bg-(--nav-active-bg-color)',
    // Minimized gap
    minimized && 'gap-0',
  );

  const hasActions = !minimized && ((subLinks?.length ?? 0) > 0 || addLink != null);

  const subLinkStyles = {
    '--height': (subLinksRef.current?.scrollHeight ?? 0) + 'px',
  } as CSSProperties;
  return (
    <div
      style={subLinkStyles}
      onMouseEnter={() => {
        if (minimized) {
          setIsOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (minimized) {
          setIsOpen(false);
        }
      }}>
      <Tooltip tooltip={minimized ? children : undefined}>
        <div className="group relative w-full">
          <Link href={href} className={classList} {...props}>
            <span className={cn('size-6', gradientActive && 'text-indigo-600 dark:text-indigo-400')}>{icon}</span>
            <span
              className={cn(
                'whitespace-pre transition-opacity duration-200 ease-[cubic-bezier(0.05,0.61,0.41,0.95)]',
                minimized && 'opacity-0',
                gradientActive && 'font-bold text-indigo-600 dark:text-indigo-400',
              )}>
              {children}
            </span>
          </Link>
          {!loading && !!counterValue && (
            <NotificationLabel
              className={cn('pointer-events-none transition-all duration-200', minimized && '-right-5')}
              counterValue={counterValue}
              gradient={gradientBadge}
            />
          )}
          {hasActions && (
            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-1 opacity-90 transition-opacity group-hover:opacity-100">
              {addLink && (
                <ButtonLink
                  href={addLink}
                  size="sm"
                  text
                  tooltip={addLinkLabel}
                  onClick={() => setIsOpen(!isOpen)}
                  icon={<General.PlusCircle />}
                />
              )}
              {(subLinks?.length ?? 0) > 0 && (
                <Button
                  text
                  size="sm"
                  onClick={() => setIsOpen(!isOpen)}
                  icon={<Arrows.ChevronDown className={cn('size-5 transition-all', { 'rotate-180': isOpen })} />}
                />
              )}
            </div>
          )}
          {newBadge && (
            <div
              className={cn(
                'pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 rounded-md px-1 py-0.5 text-2xs font-semibold uppercase',
                'bg-linear-to-r from-purple-600 to-zumthor-600 text-white',
                { hidden: minimized },
              )}>
              {' '}
              new{' '}
            </div>
          )}
        </div>
      </Tooltip>
      {(subLinks?.length ?? 0) > 0 && (
        <div
          className="overflow-hidden transition-[height] duration-200 ease-out"
          style={isOpen ? { height: 'auto' } : { height: '0px' }}>
          <div
            ref={subLinksRef}
            className={cn('flex flex-col items-stretch gap-1 pb-1.5 pl-2.5 pt-2.5', minimized && 'pl-0')}>
            {subLinks?.map((el) => {
              const subKey = typeof el.href === 'string' ? el.href : (el.href?.pathname ?? '');
              return (
                <LeftNavRow subLinks={el.subLinks} href={el.href} icon={el.icon} minimized={minimized} key={subKey}>
                  {el.children}
                </LeftNavRow>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getSublinksUrls = (subLinks?: RowProps[]) => {
  if (!subLinks) return [];

  const sublinksUrls: string[] = [];
  const checkSublinks = (subLinks: RowProps[]) => {
    subLinks.forEach((el) => {
      if (el.href && typeof el.href === 'string') sublinksUrls.push(el.href);
      if (el.subLinks) checkSublinks(el.subLinks);
    });
  };
  checkSublinks(subLinks);
  return sublinksUrls;
};
